import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  Project,
  Task,
  ProjectMember,
  DirectorySearchResult,
  NotificationPreference,
  AuditLogEntry,
  ProjectProgress,
  CurrentUser,
} from '@/types'

const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
})

export function setupApiInterceptors(getAccessToken: () => Promise<string>) {
  apiClient.interceptors.request.use(
    async (config) => {
      try {
        const token = await getAccessToken()
        config.headers.Authorization = `Bearer ${token}`
      } catch (error) {
        console.error('Failed to acquire token:', error)
        window.location.href = '/.auth/login/aad'
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  apiClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        window.location.href = '/.auth/login/aad'
      }
      return Promise.reject(error)
    },
  )
}

export const projectApi = {
  getProjects: () => apiClient.get<{ projects: Project[]; total: number }>('/projects'),
  createProject: (data: { name: string; description?: string; acquiredCompanyName: string; templateId?: string }) =>
    apiClient.post<Project>('/projects', data),
  getProject: (id: string) => apiClient.get<Project>(`/projects/${id}`),
  updateProject: (id: string, data: Partial<Project>) =>
    apiClient.put<Project>(`/projects/${id}`, data),
  deleteProject: (id: string) => apiClient.delete(`/projects/${id}`),
  getProgress: (id: string) => apiClient.get<ProjectProgress>(`/projects/${id}/progress`),
}

export const taskApi = {
  getTasks: (projectId: string, filters?: Record<string, string>) =>
    apiClient.get<{ tasks: Task[]; total: number }>(`/projects/${projectId}/tasks`, { params: filters }),
  updateTask: (projectId: string, taskId: string, data: Partial<Task>) =>
    apiClient.put<Task>(`/projects/${projectId}/tasks/${taskId}`, data),
  assignTask: (projectId: string, taskId: string, data: { entraObjectId: string; entraType: string; sendNotification?: boolean }) =>
    apiClient.post(`/projects/${projectId}/tasks/${taskId}/assign`, data),
  exportTasks: (projectId: string) =>
    apiClient.get(`/projects/${projectId}/tasks/export`, { responseType: 'blob' }),
  bulkUpdate: (projectId: string, data: { taskIds: string[]; status: string }) =>
    apiClient.post(`/projects/${projectId}/tasks/bulk-update`, data),
}

export const memberApi = {
  getMembers: (projectId: string) =>
    apiClient.get<{ members: ProjectMember[]; total: number }>(`/projects/${projectId}/members`),
  addMember: (projectId: string, data: { entraObjectId: string; entraType: string; role: string; sendNotification?: boolean }) =>
    apiClient.post<ProjectMember>(`/projects/${projectId}/members`, data),
  removeMember: (projectId: string, memberId: string) =>
    apiClient.delete(`/projects/${projectId}/members/${memberId}`),
  updateRole: (projectId: string, memberId: string, data: { role: string }) =>
    apiClient.put<ProjectMember>(`/projects/${projectId}/members/${memberId}/role`, data),
}

export const userApi = {
  searchDirectory: (query: string, type?: string) =>
    apiClient.get<{ results: DirectorySearchResult[]; total: number }>('/directory/search', {
      params: { q: query, type },
    }),
  getCurrentUser: () => apiClient.get<CurrentUser>('/users/me'),
  listUsers: (limit?: number, offset?: number) =>
    apiClient.get('/users', { params: { limit, offset } }),
}

export const notificationApi = {
  getPreferences: () => apiClient.get<NotificationPreference>('/notifications/preferences'),
  updatePreferences: (data: Partial<NotificationPreference>) =>
    apiClient.put<NotificationPreference>('/notifications/preferences', data),
  sendTestNotification: () => apiClient.post('/notifications/test'),
  getLog: (limit?: number, offset?: number) =>
    apiClient.get('/admin/notifications/log', { params: { limit, offset } }),
}

export const auditApi = {
  getLog: (filters?: Record<string, string>) =>
    apiClient.get<{ entries: AuditLogEntry[]; nextCursor?: string }>('/admin/audit', { params: filters }),
  export: (filters?: Record<string, string>) =>
    apiClient.get('/admin/audit/export', { params: filters, responseType: 'blob' }),
}

export default apiClient
